import {useEffect, useState} from "react";
import SuspensePopup from "../../SuspensePopup";
import {getBenefit} from "../../../api/benefit/benefit.api";
import NoImage from "../../../assets/images/no-image.svg";
import {displayBenefitName, isBlankName} from "../../../libs/benefitName";

/**
 * 혜택 목록의 (i) 버튼이 여는 '혜택 정보' 팝업.
 *
 * 제목 = '혜택 정보(ID)' (ID 는 강조 없이 제목에만, 2026-11-03 2차 지시),
 * 가운데 = 혜택 이미지(`prize_image`, 고객이 당첨 화면에서 보는 이미지), 하단 = 소비자가·원가(작게). 2026-11-03 사용자 지시 — 소비자가는 고객사가 거의 쓰지 않는 값이라
 * 목록 칸('미설정' 이 대부분이었다)에서 빼고 이 팝업으로 옮겼다.
 *
 * `benefit` 은 목록 행(`BenefitListResponse`)이다. id·이름·타입·가격은 목록에 이미 있으므로
 * 팝업을 **즉시** 그리고, 목록에 없는 이미지만 상세 API 로 받아 채운다(이미지 자리는 그동안 빈 틀).
 */
const BenefitInfoPopup = ({benefit, onClose}) => {
  const [detail, setDetail] = useState(null)

  useEffect(() => {
    setDetail(null)
    if (!benefit) return
    let alive = true
    getBenefit(benefit.id).then(res => {
      if (alive && res?.data) setDetail(res.data)
    })
    return () => { alive = false }
  }, [benefit])

  if (!benefit) return null

  const priceRows = describePrices(benefit)

  return (
      <SuspensePopup title={`혜택 정보(${benefit.id})`} visible={true} onClose={onClose}>
        <div className="flex-col items-center gap-4 py-4" data-testid="benefit-info">
          <div className={`text-sm text-center ${isBlankName(benefit.name) ? "text-gray-400 italic" : "text-gray-600"}`}>{displayBenefitName(benefit.name)}</div>
          <div className="w-full max-w-[320px] rounded-lg overflow-hidden bg-gray-50 border border-gray-100 flex-center-all" style={{minHeight: "160px"}}>
            {
              detail === null
                  ? <div className="text-xs text-gray-400">이미지 불러오는 중…</div>
                  : <img className="w-full" alt={benefit.name} src={detail.prizeImage || NoImage} data-testid="benefit-info-image" />
            }
          </div>
          {
            priceRows.length > 0 && (
                <div className="flex gap-4 text-xs text-gray-500" data-testid="benefit-info-prices">
                  {priceRows.map(row => (
                      <div key={row.label} className="flex gap-1">
                        <span>{row.label}</span>
                        <span className="font-semibold text-gray-700">{row.value}</span>
                      </div>
                  ))}
                </div>
            )
          }
        </div>
      </SuspensePopup>
  )
}

/**
 * 팝업 하단의 가격 줄. **값이 있을 때만** 줄을 만든다(사용자 지시: "있다면 보여준다").
 * `null`·`0` 은 둘 다 '없음' 으로 본다 — 서버가 자동 생성하는 혜택은 `retailPrice = 0` 고정이고
 * 고객 화면(`static/front/assets/benefit.js`)도 0 이면 가격을 숨기므로 그 기준과 맞춘다.
 * 포인트형은 `supply_price` 가 원가가 아니라 지급 포인트라 라벨을 바꾼다(`BenefitDefaultSettings` 와 동일).
 */
export function describePrices(benefit) {
  const rows = []
  const retail = Number(benefit.retailPrice)
  const supply = Number(benefit.supplyPrice)
  if (retail > 0) rows.push({label: "소비자가", value: `${retail.toLocaleString()}원`})
  if (supply > 0) {
    rows.push(benefit.type === "POINT"
        ? {label: "포인트", value: `${supply.toLocaleString()}P`}
        : {label: "원가", value: `${supply.toLocaleString()}원`})
  }
  return rows
}

export default BenefitInfoPopup
